import "./Copyable.scss";
import { Button, type GetProps, Tooltip, Typography } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { copyTextToClipboard } from "utils/strings";
import { useState } from "react";

const { Paragraph } = Typography;

type CopyableProps = Omit<GetProps<typeof Paragraph>, "copyable"> & { copyText?: string };

interface CopyableTextProps extends CopyableProps {
    readonly ref?: React.Ref<HTMLElement>;
    readonly icon?: React.ReactNode;
}

/** Helper to make text easy to copy to the clipboard. Based on AntD {@link Paragraph}; most props can be forwarded. */
export const Copyable = ({ children, copyText, icon, ...props }: CopyableTextProps) => (
    <Paragraph copyable={{ text: copyText, icon, tooltips: ["Click to Copy", "Copied to Clipboard"] }} {...props}>
        {children}
    </Paragraph>
);

interface CopyableButtonProps {
    copyText: string;
    children: React.ReactNode;
    className?: string;
}

/** Button that copies text to the clipboard when clicked. */
export const CopyableButton = ({ children, copyText, className }: CopyableButtonProps) => {
    const [isCopied, setIsCopied] = useState(false);
    const [copyError, setCopyError] = useState(false);

    return (
        <Tooltip
            rootClassName="vyne-copyable-button-tooltip"
            arrow={false}
            title={!copyError ? "Successfully copied to clipboard" : "Failed to copy"}
            placement="bottom"
            open={isCopied}
        >
            <Button
                className={`vyne-copyable-button ${className ?? ""}`}
                type="default"
                onClick={() => void handleCopyClick(copyText, setIsCopied, setCopyError)}
            >
                <CopyOutlined />
                {children}
            </Button>
        </Tooltip>
    );
};

/** Copies the given text to the clipboard and sets the copy state. */
async function handleCopyClick(
    copyText: string,
    setIsCopied: (value: boolean) => void,
    setCopyError: (value: boolean) => void,
) {
    try {
        await copyTextToClipboard(copyText);
        setIsCopied(true);
    } catch (e) {
        console.warn("Failed to copy text to clipboard", e);
        setCopyError(true);
    } finally {
        setTimeout(() => {
            setCopyError(false);
            setIsCopied(false);
        }, 3000);
    }
}
